<script setup>
import { getCurrentInstance, ref, computed } from 'vue';
import { usePlaylistStore } from "@/stores/playlist.js";
import { enableScroll, scrollToSlide } from '@/Helpers/scroll.js';
import { resolveColourValue } from '@/Helpers/themeColourResolver';
const plStore = usePlaylistStore();

const props = defineProps({
    row: {
        type: Object,
        default: undefined,
    },
    brand: {
        type: Object,
        default: undefined,
    },
    shift: { type: Number, default: 0 },
    scormExport: Boolean,
});

const instance = getCurrentInstance();
const scormApi = ref(null);
if (props.scormExport) {
    scormApi.value = instance.appContext.config.globalProperties.$scorm.api;
}

const buttonColourHex = computed(() => {
    const value = props.row?.content?.buttonColour;
    if (!value) return '';
    return resolveColourValue(value, props.brand, props.shift);
});

const textColourHex = computed(() => {
    const value = props.row?.content?.textColour;
    if (!value) return '';
    return resolveColourValue(value, props.brand, props.shift);
});

const buttonStyle = computed(() => {
    const style = {};
    if (buttonColourHex.value) {
        style.backgroundColor = buttonColourHex.value;
        style.borderColor = buttonColourHex.value;
    }
    if (textColourHex.value) {
        style.color = textColourHex.value;
    }
    if (props.row?.content?.buttonFont) {
        style.fontFamily = props.row.content.buttonFont.replace(/_/g, ' ');
    }
    style.fontWeight = props.row?.content?.buttonBold === false ? '400' : '700';
    style.fontStyle = props.row?.content?.buttonItalic ? 'italic' : 'normal';
    return style;
});

const buttonFontSizeClass = computed(() => props.row?.content?.buttonFontSize || '');

const buttonClicked = (button) => {
    switch (button.buttonType) {
    case 'web':
        window.open(button.buttonLocation, '_blank');
        break;

    case 'file':
        window.open(button.buttonLocation, '_blank');
        break;

    case 'card':
        const cardIndex = plStore.cards.findIndex(card => card.card_key === button.buttonLocation);
        if (cardIndex !== -1) {
            enableScroll();
            scrollToSlide(cardIndex);
        } else {
            alert('Card not found: ' + button.buttonLocation);
        }
        break;

    case 'playlist':
        if (button.buttonLocation) {
            window.location.href = button.buttonLocation;
        }
        break;

    case 'dashboard':
        // The learner dashboard does not exist inside an offline SCORM package.
        if (!props.scormExport && button.buttonLocation) {
            window.location.href = button.buttonLocation;
        }
        break;

    case 'certificates':
        // The certificates page does not exist inside an offline SCORM package.
        // The destination is resolved per-context by the server (journey vs standalone).
        if (!props.scormExport && plStore.certificatesUrl) {
            window.location.href = plStore.certificatesUrl;
        }
        break;

    case 'finishScorm':
        if (props.scormExport && scormApi.value) {
            scormApi.value.finish();
        }
        break;
    }
};

function isFaIcon(value) {
    return typeof value === 'string' && value.startsWith('fa-');
}
</script>
<template>
    <div :class="'w-full grid grid-cols-' + row.content.columns + ' justify-items-' + row.content.alignment + ' items-center gap-4 p-fl-4'">
        <div
            v-for="(button, index) in row.content.buttons"
            :key="index"
            :class="[
                'h-fit',
                row.content.columns == 1 && row.content.fullWidth ? 'w-full' : 'w-fit'
            ]"
        >
            <button
                type="button"
                class="rounded-full
                    border-2
                    focus-visible:outline
                    focus-visible:outline-2
                    focus-visible:outline-offset-2
                    focus-visible:outline-white cursor-pointer
                    transition-all duration-300 ease-in-out
                    flex items-center gap-2 relative"
                :class="[
                    row.content.reducedPadding ? 'px-fl-1 py-fl-1' : 'px-fl-4 py-fl-2',
                    row.content.shadow ? 'has-shadow' : '',
                    row.content.columns == 1 && row.content.fullWidth ? 'w-full' : '',
                    button.iconUrl ? 'justify-start' : 'justify-center',
                    buttonFontSizeClass
                ]"
                :style="buttonStyle"
                @click="buttonClicked(button)"
            >
                <div v-if="button.iconUrl" class="w-5 h-5 rounded-full overflow-hidden shrink-0 flex items-center justify-center">
                    <font-awesome-icon v-if="isFaIcon(button.iconUrl)" :icon="button.iconUrl" />
                    <img v-else :src="button.iconUrl" :alt="button.buttonText + ' icon'" class="w-full h-full object-cover">
                </div>
                <span
                    :class="[
                        button.iconUrl ? 'flex-1 text-center' : ''
                    ]"
                >{{ button.buttonText }}</span>
            </button>
        </div>
    </div>
</template>

<style scoped>
button {
    border-color: var(--button_bg);
    background-color: var(--button_bg);
    color: var(--button_text);
}

button.has-shadow {
    box-shadow: 0 5px 7px -2px var(--button_bg), 0 1px 2px -2px var(--button_bg);
}

button:hover {
    background-color: var(--button_hover_bg);
    color: var(--button_hover_text);
}

button.has-shadow:hover {
    box-shadow: 0 3px 5px -2px var(--button_bg), 0 1px 2px -2px var(--button_bg);
}
</style>
